import React, { useEffect, useRef } from "react"
import { Chart, registerables } from "chart.js"

// Register all Chart.js components once (same pattern as FeatureUsageChart).
Chart.register(...registerables)

// Draws a vertical dashed line at the hovered point (crosshair).
const CROSSHAIR_PLUGIN = {
  id: "crosshair",
  afterDraw(chart) {
    const active =
      (chart.tooltip && chart.tooltip.getActiveElements
        ? chart.tooltip.getActiveElements()
        : chart._active) || []
    if (!active.length) return
    const {
      ctx,
      chartArea: { top, bottom },
    } = chart
    const x = active[0].element.x
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(x, top)
    ctx.lineTo(x, bottom)
    ctx.lineWidth = 1
    ctx.strokeStyle = "rgba(100,100,100,0.35)"
    ctx.setLineDash([4, 4])
    ctx.stroke()
    ctx.restore()
  },
}

const EMPTY_PLUGIN = (lang) => ({
  id: "noDataText",
  afterDraw(chart) {
    const ds = chart.data?.datasets?.[0]?.data || []
    const total = ds.reduce((a, b) => a + (Number(b) || 0), 0)
    if (total > 0) return
    const { ctx, chartArea } = chart
    if (!chartArea) return
    const cx = (chartArea.left + chartArea.right) / 2
    const cy = (chartArea.top + chartArea.bottom) / 2
    ctx.save()
    ctx.fillStyle = "#9CA3AF"
    ctx.font = "bold 15px Arial"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(
      lang === "ar" ? "لا توجد بيانات لهذه الفترة" : "No data for this period",
      cx,
      cy
    )
    ctx.restore()
  },
})

/**
 * Line chart (DAU / WAU). data: [{ label, value }]
 */
export const PostHogLineChart = ({ data = [], color = "#7C3AED", lang = "en" }) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current) return undefined
    if (chartInstance.current) chartInstance.current.destroy()

    const ctx = chartRef.current.getContext("2d")
    const gradient = ctx.createLinearGradient(0, 0, 0, 300)
    gradient.addColorStop(0, `${color}55`)
    gradient.addColorStop(1, `${color}00`)

    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: data.map((d) => d.label),
        datasets: [
          {
            data: data.map((d) => d.value),
            borderColor: color,
            backgroundColor: gradient,
            fill: true,
            tension: 0.35,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        hover: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false, drawBorder: false },
            ticks: { font: { size: 13 }, color: "#888", maxTicksLimit: 8 },
          },
          y: {
            beginAtZero: true,
            grid: { color: "#F0F0F0", drawBorder: false },
            ticks: { font: { size: 13 }, color: "#888", precision: 0 },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: "index",
            intersect: false,
            backgroundColor: "rgba(0,0,0,0.8)",
            padding: 10,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              label: (ctx) => ` ${ctx.parsed.y}`,
            },
          },
        },
      },
      plugins: [EMPTY_PLUGIN(lang), CROSSHAIR_PLUGIN],
    })

    return () => {
      if (chartInstance.current) chartInstance.current.destroy()
    }
  }, [data, color, lang])

  return (
    <div style={{ position: "relative", width: "100%", height: "280px" }}>
      <canvas ref={chartRef} />
    </div>
  )
}

/**
 * Bar chart (event totals / referring domains / pageview funnel).
 * data: [{ label, value }]. horizontal => indexAxis 'y'.
 */
export const PostHogBarChart = ({
  data = [],
  color = "#3B82F6",
  horizontal = false,
  height = 300,
  lang = "en",
}) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current) return undefined
    if (chartInstance.current) chartInstance.current.destroy()

    const ctx = chartRef.current.getContext("2d")
    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: data.map((d) => d.label),
        datasets: [
          {
            data: data.map((d) => d.value),
            backgroundColor: color,
            borderRadius: 6,
            maxBarThickness: horizontal ? 22 : 40,
          },
        ],
      },
      options: {
        indexAxis: horizontal ? "y" : "x",
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            beginAtZero: true,
            grid: { display: !horizontal ? false : true, color: "#F0F0F0", drawBorder: false },
            ticks: { font: { size: 13 }, color: "#888", precision: 0 },
          },
          y: {
            beginAtZero: true,
            grid: { display: horizontal ? false : true, color: "#F0F0F0", drawBorder: false },
            ticks: { font: { size: 13 }, color: "#666", precision: 0 },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: { backgroundColor: "rgba(0,0,0,0.8)", padding: 10, cornerRadius: 8 },
        },
      },
      plugins: [EMPTY_PLUGIN(lang)],
    })

    return () => {
      if (chartInstance.current) chartInstance.current.destroy()
    }
  }, [data, color, horizontal, lang])

  return (
    <div style={{ position: "relative", width: "100%", height: `${height}px` }}>
      <canvas ref={chartRef} />
    </div>
  )
}

/**
 * Stacked bar chart with multiple series (e.g. New vs Returning users).
 * labels: string[]; series: [{ name, data: number[], color }].
 */
export const PostHogStackedBarChart = ({
  labels = [],
  series = [],
  height = 280,
  lang = "en",
}) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current) return undefined
    if (chartInstance.current) chartInstance.current.destroy()

    const ctx = chartRef.current.getContext("2d")
    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: series.map((s) => ({
          label: s.name,
          data: s.data,
          backgroundColor: s.color,
          borderRadius: 4,
          maxBarThickness: 26,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            stacked: true,
            grid: { display: false, drawBorder: false },
            ticks: { font: { size: 12 }, color: "#888", maxTicksLimit: 8 },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: { color: "#F0F0F0", drawBorder: false },
            ticks: { font: { size: 12 }, color: "#888", precision: 0 },
          },
        },
        plugins: {
          legend: {
            display: true,
            position: "top",
            align: lang === "ar" ? "end" : "start",
            labels: { font: { size: 12 }, color: "#666", boxWidth: 12, padding: 12 },
          },
          tooltip: {
            mode: "index",
            intersect: false,
            backgroundColor: "rgba(0,0,0,0.8)",
            padding: 10,
            cornerRadius: 8,
          },
        },
      },
      plugins: [EMPTY_PLUGIN(lang)],
    })

    return () => {
      if (chartInstance.current) chartInstance.current.destroy()
    }
  }, [labels, series, lang])

  return (
    <div style={{ position: "relative", width: "100%", height: `${height}px` }}>
      <canvas ref={chartRef} />
    </div>
  )
}

// A rotating palette for categorical slices (device types, etc.).
const DONUT_COLORS = [
  "#7C3AED",
  "#2563EB",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#EC4899",
  "#14B8A6",
  "#8B5CF6",
]

/**
 * Doughnut chart for categorical breakdowns (e.g. device types).
 * data: [{ label, value }].
 */
export const PostHogDoughnutChart = ({ data = [], height = 280, lang = "en" }) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current) return undefined
    if (chartInstance.current) chartInstance.current.destroy()

    const ctx = chartRef.current.getContext("2d")
    chartInstance.current = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: data.map((d) => d.label),
        datasets: [
          {
            data: data.map((d) => d.value),
            backgroundColor: data.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]),
            borderColor: "#FFFFFF",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        plugins: {
          legend: {
            position: lang === "ar" ? "left" : "right",
            labels: { font: { size: 13 }, color: "#666", boxWidth: 14, padding: 12 },
          },
          tooltip: { backgroundColor: "rgba(0,0,0,0.8)", padding: 10, cornerRadius: 8 },
        },
      },
      plugins: [EMPTY_PLUGIN(lang)],
    })

    return () => {
      if (chartInstance.current) chartInstance.current.destroy()
    }
  }, [data, lang])

  return (
    <div style={{ position: "relative", width: "100%", height: `${height}px` }}>
      <canvas ref={chartRef} />
    </div>
  )
}
